import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import type { ConnectionHookResult, ProviderCode } from '@interfaces/Integration';
import { MetaCloudConnectionState, MetaCloudConnectionResponse } from '../types';
import { useSyncIntegration, useRemoveIntegration, useGetIntegrations } from '../../../../hooks';
import { PROVIDER_TYPE_MAP, Integration } from '@utils/constants';
import { loadFacebookSdk, launchEmbeddedSignup, META_EMBEDDED_SIGNUP_CONFIG_ID } from '../constants';

// Categoría única para agrupar los breadcrumbs del flujo de conexión en Sentry.
const SENTRY_CATEGORY = 'metaCloudApi.connect';

const GENERIC_CONNECT_ERROR = 'Error al conectar con Meta. Intenta nuevamente.';

const INITIAL_STATE: MetaCloudConnectionState = {
  connectionStatus: 'DISCONNECTED',
  isConnecting: false,
  phoneNumber: null,
  instanceName: null,
  error: null,
};

interface UseMetaCloudConnectionProps {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: Record<string, unknown>;
}

export const useMetaCloudConnection = (integration?: UseMetaCloudConnectionProps): ConnectionHookResult => {
  const { syncIntegration } = useSyncIntegration();
  const { removeIntegration } = useRemoveIntegration();
  const { refreshIntegrations } = useGetIntegrations();

  const lastCheckedIdRef = useRef<string | null>(null);
  const isDisconnectingRef = useRef(false);

  const [isCheckingStatus, setIsCheckingStatus] = useState(() => Boolean(integration?.id));

  const [state, setState] = useState<MetaCloudConnectionState>(() => {
    if (integration?.id) {
      return {
        ...INITIAL_STATE,
        connectionStatus: 'CONNECTED',
        instanceName: (integration.configuration?.instance_name as string) || null,
        phoneNumber: (integration.configuration?.phone_number as string) || null,
      };
    }
    return { ...INITIAL_STATE };
  });

  const handleConnectionResponse = useCallback(
    (response: MetaCloudConnectionResponse, showToast = false) => {
      if (response.status === 'CONNECTED') {
        setState((prev) => {
          if (isDisconnectingRef.current) return prev;
          if (prev.connectionStatus === 'CONNECTED') return prev;

          return {
            ...prev,
            isConnecting: false,
            connectionStatus: 'CONNECTED',
            instanceName: response.instanceName || null,
            phoneNumber: response.phoneNumber || null,
            error: null,
          };
        });

        if (!isDisconnectingRef.current) {
          refreshIntegrations();
          if (showToast) {
            toast.success('¡WhatsApp Cloud API conectado!');
          }
        }
      } else if (response.status === 'DISCONNECTED' || response.status === 'ERROR') {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          connectionStatus: 'DISCONNECTED',
          error: response.message || 'Desconectado',
        }));
      }
    },
    [refreshIntegrations],
  );

  const fetchStatus = useCallback(async () => {
    try {
      const response = (await syncIntegration(integration as unknown as Integration, {
        action: 'SHOW_STATUS',
        notify: false,
      })) as unknown as MetaCloudConnectionResponse;

      if (response) {
        handleConnectionResponse(response);
        return response;
      }
    } catch {
      return null;
    } finally {
      setIsCheckingStatus(false);
    }
  }, [integration, syncIntegration, handleConnectionResponse]);

  // Check real status on mount — once per ID
  useEffect(() => {
    if (integration?.id && integration.id !== lastCheckedIdRef.current) {
      lastCheckedIdRef.current = integration.id;
      fetchStatus();
    }
  }, [integration?.id, fetchStatus]);

  // Preload Facebook SDK when component mounts
  useEffect(() => {
    loadFacebookSdk().catch(() => {
      // SDK load failure is non-fatal; error will surface when user clicks Connect
    });
  }, []);

  /**
   * Opens Meta's Embedded Signup popup via FB.login().
   * The code comes back via the JS callback — no redirect needed.
   *
   * Instrumentado para observabilidad: breadcrumbs por paso y `Sentry.captureException`
   * en los dos fallos que antes quedaban invisibles (error de Meta dentro del popup y
   * fallo en el exchange con el backend). El cancel del usuario NO es error: solo deja
   * un breadcrumb y resetea silenciosamente.
   */
  const connect = useCallback(async () => {
    setState((prev) => ({ ...prev, isConnecting: true, error: null }));

    Sentry.addBreadcrumb({ category: SENTRY_CATEGORY, level: 'info', message: 'connect:start' });

    try {
      Sentry.addBreadcrumb({ category: SENTRY_CATEGORY, level: 'info', message: 'loadFacebookSdk' });
      await loadFacebookSdk();

      Sentry.addBreadcrumb({ category: SENTRY_CATEGORY, level: 'info', message: 'launchEmbeddedSignup' });
      const signup = await launchEmbeddedSignup();

      // CANCEL — el comercio cerró el popup. Acción esperada del usuario, no es error.
      if (signup.status === 'CANCEL') {
        Sentry.addBreadcrumb({
          category: SENTRY_CATEGORY,
          level: 'info',
          message: 'embedded_signup:cancel',
          data: { currentStep: signup.currentStep ?? null },
        });
        setState((prev) => ({ ...prev, isConnecting: false }));
        return;
      }

      // ERROR (o FINISH sin code, defensivo) — Meta reportó un fallo dentro del popup.
      // Capturamos la causa real en Sentry y la mostramos al comercio (no el genérico).
      if (signup.status === 'ERROR' || !signup.code) {
        const errorMessage =
          signup.errorMessage || 'No se pudo completar la conexión con WhatsApp. Intenta nuevamente.';

        Sentry.captureException(new Error(`Meta Embedded Signup falló: ${errorMessage}`), {
          tags: { provider: 'metaCloudApi', step: 'embedded_signup', currentStep: signup.currentStep },
          extra: {
            errorMessage,
            configId: META_EMBEDDED_SIGNUP_CONFIG_ID,
            signupStatus: signup.status,
          },
        });

        setState((prev) => ({
          ...prev,
          isConnecting: false,
          connectionStatus: 'ERROR',
          error: errorMessage,
        }));
        toast.error(errorMessage);
        return;
      }

      // FINISH — code recibido. Pasamos waba_id / phone_number_id (Priority 1 del backend)
      // cuando el message event los entregó; si no, el backend hace su discovery (fallback).
      Sentry.addBreadcrumb({
        category: SENTRY_CATEGORY,
        level: 'info',
        message: 'code recibido',
        data: { hasWabaId: Boolean(signup.wabaId), hasPhoneNumberId: Boolean(signup.phoneNumberId) },
      });

      Sentry.addBreadcrumb({ category: SENTRY_CATEGORY, level: 'info', message: 'backend CONNECT' });
      const response = (await syncIntegration(
        (integration || {
          provider_code: 'metaCloudApi',
          provider_type: 'communication',
        }) as unknown as Integration,
        {
          action: 'CONNECT',
          notify: false,
          config: {
            code: signup.code,
            waba_id: signup.wabaId,
            phone_number_id: signup.phoneNumberId,
          },
        },
      )) as unknown as MetaCloudConnectionResponse;

      if (response) {
        handleConnectionResponse(response, true);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { provider: 'metaCloudApi', step: 'connect' },
      });
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        connectionStatus: 'ERROR',
        error: GENERIC_CONNECT_ERROR,
      }));
      toast.error(GENERIC_CONNECT_ERROR);
    }
  }, [integration, syncIntegration, handleConnectionResponse]);

  const disconnect = useCallback(async () => {
    isDisconnectingRef.current = true;

    if (!integration?.id) {
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
      isDisconnectingRef.current = false;
      return;
    }

    setState((prev) => ({ ...prev, isConnecting: true }));

    try {
      await removeIntegration(integration as unknown as Integration);
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
    } catch {
      setState((prev) => ({ ...prev, isConnecting: false }));
    } finally {
      isDisconnectingRef.current = false;
    }
  }, [integration, removeIntegration]);

  return {
    isConnected: state.connectionStatus === 'CONNECTED',
    isConnecting: state.isConnecting,
    isCheckingStatus,
    connect,
    disconnect,
    status: state.connectionStatus,
    instanceName: state.instanceName,
    // OAuth popup flow: no QR needed
    isQrVisible: false,
    qrCode: null,
    onVerify: undefined,
    onCloseQr: undefined,
    isVerifying: false,
  };
};
